import { jwtDecode } from 'jwt-decode';

import theme from '@/assets/theme';
import { getActiveToken } from '@/common/helper/tokenStorage';
import { VariableType } from '@/proto/common';
import { themeT } from '@/utils/themeText';
export interface DerivedVariableDesc {
  fieldName: string;
  displayName: string;
  type: VariableType;
  group?: boolean;
  isMultiSelect?: boolean;
  /**
   * When true, the field has a fixed set of values (single-select enum).
   * Operators: Is, Is not, Exists, Does not exist (no Contains).
   * When false/undefined on a STRING/TEXT field, free-text operators (incl. Contains) are used.
   */
  isEnum?: boolean;
  /** Example value shown in the variable picker. */
  exampleString?: string;
}

/**
 * Derived variables available for each meta-variable type.
 * Field names must match the backend catalog in meta_variables/catalog.py.
 */
const META_VARIABLE_CATALOG: Partial<
  Record<VariableType, DerivedVariableDesc[]>
> = {
  [VariableType.SHIPMENT]: [
    {
      displayName: `${themeT('tracking', theme.themeType)} ID`,
      fieldName: 'tracking_id',
      exampleString: 'ZL-73KD92',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: `${themeT('reference', theme.themeType)} number`,
      fieldName: 'reference_number',
      exampleString: 'SHP-2026-04812',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: `${themeT('transportProvider', theme.themeType)}`,
      fieldName: 'transport_provider',
      exampleString: 'FedEx',
      group: true,
      isMultiSelect: false,
      type: VariableType.TRANSPORT_PROVIDER,
    },
    {
      displayName: `${themeT('transportProvider', theme.themeType)} name`,
      fieldName: 'transport_provider_name',
      exampleString: 'World Courier',
      group: true,
      isMultiSelect: false,
      type: VariableType.STRING,
    },
    {
      displayName: `${themeT('transportProvider', theme.themeType)} service level`,
      fieldName: 'transport_provider_service_level',
      exampleString: 'Priority Overnight',
      group: true,
      isMultiSelect: false,
      isEnum: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Products',
      fieldName: 'products',
      exampleString: 'Zepbound 5 mg',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Products count',
      fieldName: 'products_count',
      exampleString: '14',
      group: false,
      isMultiSelect: false,
      type: VariableType.NUMBER,
    },
    {
      displayName: 'Products Type',
      fieldName: 'products_type',
      exampleString: 'Refrigerated 2-8 C',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: `${themeT('team', theme.themeType)}s`,
      fieldName: 'teams',
      exampleString: 'West Coast Ops',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: `${themeT('team', theme.themeType)}s count`,
      fieldName: 'teams_count',
      exampleString: '2',
      group: false,
      isMultiSelect: false,
      type: VariableType.NUMBER,
    },
    {
      displayName: 'Status',
      fieldName: 'status',
      exampleString: 'In transit',
      group: false,
      isMultiSelect: false,
      isEnum: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Timeliness',
      fieldName: 'timeliness',
      exampleString: 'On time',
      group: false,
      isMultiSelect: false,
      isEnum: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Origin name',
      fieldName: 'origin_name',
      exampleString: 'Clinic DC 22',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Origin address',
      fieldName: 'origin_address',
      exampleString: '555 Main St, Suite 900, San Francisco, CA 94108',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Origin state',
      fieldName: 'origin_state',
      exampleString: 'CA',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Origin contact',
      fieldName: 'origin_contact',
      exampleString: 'John Doe, +1 (555) 555-5555',
      group: false,
      isMultiSelect: false,
      type: VariableType.STRING,
    },
    {
      displayName: 'Origin location type',
      fieldName: 'origin_location_type',
      exampleString: 'Distribution center',
      group: false,
      isMultiSelect: false,
      isEnum: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Destination name',
      fieldName: 'destination_name',
      exampleString: 'Warehouse 1 #214',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Destination address',
      fieldName: 'destination_address',
      exampleString: '555 Main St, Suite 900, San Francisco, CA 94108',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Destination state',
      fieldName: 'destination_state',
      exampleString: 'CA',
      group: false,
      isMultiSelect: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Destination contact',
      fieldName: 'destination_contact',
      exampleString: 'John Doe, +1 (555) 555-5555',
      group: false,
      isMultiSelect: false,
      type: VariableType.STRING,
    },
    {
      displayName: 'Destination location type',
      fieldName: 'destination_location_type',
      exampleString: 'Pharmacy',
      group: false,
      isMultiSelect: false,
      isEnum: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Current location name',
      fieldName: 'current_location_name',
      exampleString: 'Oakland, CA hub',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Current location time',
      fieldName: 'current_location_time',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'ETA original',
      fieldName: 'eta_original',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'ETA current',
      fieldName: 'eta_current',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'ETA updated at',
      fieldName: 'eta_updated_at',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Scheduled pickup',
      fieldName: 'scheduled_pickup',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Scheduled delivery',
      fieldName: 'scheduled_delivery',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Actual pickup',
      fieldName: 'actual_pickup',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Actual delivery',
      fieldName: 'actual_delivery',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Last status updated at',
      fieldName: 'last_status_updated_at',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Created at',
      fieldName: 'created_at',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Updated at',
      fieldName: 'updated_at',
      exampleString: 'Jul 4, 3:45 PM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Deeplink',
      fieldName: 'deeplink',
      exampleString: 'app.zoomlogi.com/s/ZL-73KD92',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'POD status',
      fieldName: 'pod_status',
      exampleString: 'Signed',
      group: false,
      isMultiSelect: false,
      isEnum: true,
      type: VariableType.STRING,
    },
    {
      displayName: 'Active leg number (MBL)',
      fieldName: 'active_leg_number',
      exampleString: '24431669M',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg mode',
      fieldName: 'active_leg_mode',
      exampleString: 'Air',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg status',
      fieldName: 'active_leg_status',
      exampleString: 'In Transit',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg carrier',
      fieldName: 'active_leg_carrier',
      exampleString: 'American Airlines',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg MAWB',
      fieldName: 'active_leg_mawb',
      exampleString: '001-12345675',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg pickup office (RTG)',
      fieldName: 'active_leg_pickup_office',
      exampleString: 'WCAUS',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg drop office (RTG)',
      fieldName: 'active_leg_drop_office',
      exampleString: 'WCIND',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg origin name',
      fieldName: 'active_leg_origin_name',
      exampleString: 'AUSTIN-BERGSTROM INTERNATIONAL AIRPORT',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg origin code',
      fieldName: 'active_leg_origin_code',
      exampleString: 'AUS',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg origin city',
      fieldName: 'active_leg_origin_city',
      exampleString: 'AUSTIN, TX, US',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg origin planned',
      fieldName: 'active_leg_origin_planned',
      exampleString: 'Oct 6, 9:00 AM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Active leg destination name',
      fieldName: 'active_leg_destination_name',
      exampleString: 'INDIANAPOLIS INTERNATIONAL AIRPORT',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg destination code',
      fieldName: 'active_leg_destination_code',
      exampleString: 'IND',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg destination city',
      fieldName: 'active_leg_destination_city',
      exampleString: 'INDIANAPOLIS, IN, US',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Active leg destination planned',
      fieldName: 'active_leg_destination_planned',
      exampleString: 'Oct 7, 3:42 AM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Active leg actual pickup',
      fieldName: 'active_leg_actual_pickup',
      exampleString: 'Oct 6, 9:18 AM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Active leg actual drop',
      fieldName: 'active_leg_actual_drop',
      exampleString: 'Oct 7, 3:50 AM',
      group: false,
      isMultiSelect: false,
      type: VariableType.DATE_TIME,
    },
    {
      displayName: 'Active leg flights',
      fieldName: 'active_leg_flights',
      exampleString: '1052 AUS→CLT, 1033 CLT→IND',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Controlling office (RTG)',
      fieldName: 'controlling_office',
      exampleString: 'NYC',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
    {
      displayName: 'Controlling office email',
      fieldName: 'controlling_office_email',
      exampleString: 'nyc.office@example.com',
      group: false,
      isMultiSelect: false,
      type: VariableType.TEXT,
    },
  ],
  [VariableType.ALERT]: [
    {
      displayName: 'Alert type',
      fieldName: 'alert_type',
      exampleString: 'ETA delay',
      type: VariableType.ALERT_TYPE,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Alert stage',
      fieldName: 'alert_stage',
      exampleString: 'Escalated',
      type: VariableType.STRING,
      group: false,
      isMultiSelect: false,
      isEnum: true,
    },
    {
      displayName: 'Alert state',
      fieldName: 'alert_state',
      exampleString: 'Open',
      type: VariableType.STRING,
      group: false,
      isMultiSelect: false,
      isEnum: true,
    },
    {
      displayName: 'Severity',
      fieldName: 'severity',
      exampleString: 'High',
      type: VariableType.STRING,
      group: false,
      isMultiSelect: false,
      isEnum: true,
    },
    {
      displayName: 'Created at',
      fieldName: 'created_at',
      exampleString: 'Jul 4, 3:45 PM',
      type: VariableType.DATE_TIME,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Threshold value',
      fieldName: 'threshold_value',
      exampleString: '120',
      type: VariableType.NUMBER,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Current value',
      fieldName: 'current_value',
      exampleString: '315',
      type: VariableType.NUMBER,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Title',
      fieldName: 'title',
      exampleString: 'Delivery at risk: SHP-2026-04812',
      type: VariableType.TEXT,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Detail',
      fieldName: 'detail',
      exampleString: 'ETA slipped 5h 15m past the delivery window',
      type: VariableType.TEXT,
      group: false,
      isMultiSelect: false,
    },
    {
      // Detail without its advice: a weather alert's "L4 risk: Consider alternate
      // route. " prefix or another alert's appended recommended action.
      displayName: 'Headline',
      fieldName: 'headline',
      exampleString: 'Winter Storm Warning issued October 30 by NWS Upton NY',
      type: VariableType.TEXT,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Recommended action',
      fieldName: 'recommended_action',
      exampleString: 'Call courier to confirm recovery ETA',
      type: VariableType.TEXT,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Is critical',
      fieldName: 'is_critical',
      exampleString: 'true',
      type: VariableType.BOOL,
      group: false,
      isMultiSelect: false,
    },
    {
      displayName: 'Is resolved',
      fieldName: 'is_resolved',
      exampleString: 'false',
      type: VariableType.BOOL,
      group: false,
      isMultiSelect: false,
    },
  ],
};

/** Shipment fields read off the active execution leg (CL/EL companies only). */
const ACTIVE_LEG_FIELD_PREFIX = 'active_leg_';

/** Shipment fields only CL/EL companies have: the active leg's, and the controlling
 * office (from the active leg's RTG offices) with the address its email goes to. */
function isCustomerLegField(fieldName: string): boolean {
  return (
    fieldName.startsWith(ACTIVE_LEG_FIELD_PREFIX) ||
    fieldName === 'controlling_office' ||
    fieldName === 'controlling_office_email'
  );
}

/** Whether the signed-in company has customer-leg / execution-leg (CL/EL) enabled. */
function customerLegEnabled(): boolean {
  const token = getActiveToken();
  if (!token) return false;
  try {
    return Boolean(
      jwtDecode<{ enablecustomerleg?: boolean }>(token).enablecustomerleg
    );
  } catch {
    return false;
  }
}

/**
 * Returns the derived variables for a meta-variable type, or undefined
 * if the type has no derived variables. This is the full catalog, used to
 * look up and type-check existing references.
 */
export function getDerivedVariables(
  type: VariableType
): DerivedVariableDesc[] | undefined {
  return META_VARIABLE_CATALOG[type];
}

/**
 * The derived variables the variable picker offers for a meta-variable type.
 * The active-leg and controlling office shipment fields are offered only to
 * companies with CL/EL enabled; the backend resolves them only for those companies too, so an
 * existing reference elsewhere still validates and reads as "does not exist".
 */
export function getPickableDerivedVariables(
  type: VariableType
): DerivedVariableDesc[] | undefined {
  const fields = META_VARIABLE_CATALOG[type];
  if (type !== VariableType.SHIPMENT || !fields || customerLegEnabled()) {
    return fields;
  }
  return fields.filter((field) => !isCustomerLegField(field.fieldName));
}
